/**
 * ADR 170/185: a rack volume from the backend
 * (`GET …/warehouses/{warehouseId}/structure/volumes`) and its floor-plane shape
 * frame. `footprint` is `[[x,y], …]` meters in the warehouse frame (already placed
 * through the map's ADR 169 calibration), `baseM` the level floor and `heightM` the
 * clear height. This module is the canonical type — rest.ts re-exports it so the API
 * and the scene can't drift (mirrors OccupancyCell).
 *
 * Kept free of three.js/React so the coordinate convention — which must match
 * OccupancyLayer.polygonToShape exactly, or a rack would not land on its bin — is
 * unit-tested (mirrors rackLayout.ts vs renderRackSnapshot.ts).
 */
export interface RackVolume {
  /** Storage-location id ({mapId}|{address}) — the ADR 186 height-PATCH handle.
   *  Optional: absent on payloads older than ADR 190. */
  id?: string;
  address: string;
  aisleLabel?: string | null;
  side?: string | null;
  level: number;
  nodeId?: string | null;
  /** The bound node's AISLE ancestor — the id `AisleMetrics.id` carries, so
   *  id-based consumers (KPI badges, zone dock) join without the label's
   *  same-code ambiguity. Null on unbound locations and pre-ADR-281 payloads. */
  aisleNodeId?: string | null;
  /** Level floor elevation, meters. */
  baseM: number;
  /** Clear height of the slot, meters — the box grows this far above baseM. */
  heightM: number;
  /** `[[x,y], …]` meters, warehouse frame. */
  footprint: number[][];
  /** ADR 425: 2 on a double-deep bin; absent = one pallet position. */
  depthSlots?: number;
}

/** The top of the racking: the highest slot ceiling. Null when there is nothing to measure. */
export function structureTopOf(volumes: RackVolume[]): number | null {
  let top: number | null = null;
  for (const v of volumes) {
    const t = v.baseM + v.heightM;
    if (Number.isFinite(t) && (top === null || t > top)) top = t;
  }
  return top;
}

/**
 * Warehouse-frame `[[x,y]]` → floor-plane shape points, negating `y`. The scene's
 * `rotation-x = -π/2` ground group maps a flat shape's local (x, y) to world (x, -y);
 * every other floor consumer places meters (x, y) at world (x, z) with no flip, so `y`
 * is negated here to cancel it — the identical move OccupancyLayer.polygonToShape makes.
 */
export function footprintToShapePoints(footprint: number[][]): [number, number][] {
  return footprint.map(([x, y]) => [x, -y]);
}
